<template>
	<v-container :class="{ 'py-0': omitCreation || lockedProject }">
		<v-row v-if="!omitCreation && !lockedProject" class="mb-5">
			<v-select
				v-model="attachAction"
				:items="attachActions"
				label="Attach to project"
				:persistent-hint="true"
				hint="Papers should be attached to at least one project."
				:menu-props="{ offsetY: true }"
				prepend-icon="mdi-file-tree-outline"
			/>
		</v-row>

		<v-row
			v-if="attachAction === 'EXIST' || omitCreation || lockedProject"
			class="my-5 mb-10"
		>
			<!-- DCTL-2035/DCTL-2495: when lockedProject is set (add/edit paper from
			     a project context) that project is pinned - pre-selected and not
			     removable - while other projects can still be added or removed. -->
			<project-search
				v-model="projectSelects"
				:default-projects="projects"
				:disabled="!!submitting"
				:pinned-project-uuid="lockedProject ? lockedProject.uuid : null"
			/>
		</v-row>

		<v-row v-else-if="attachAction === 'NEW' && !omitCreation">
			<v-col class="pa-0" cols="12">
				<project-edit
					ref="projectEdit"
					v-model:dirty="subDirty"
					v-model:invalid="subInvalid"
					nested
					no-actions
				/>
			</v-col>
		</v-row>
	</v-container>
</template>

<script>
import _ from "lodash";
import { mapActions, mapState } from "pinia";
import { useMainStore } from "@/store";

import ProjectEdit from "@/pages/ProjectManager/ProjectEdit";
import ProjectSearch from "@/pages/ProjectManager/ProjectSearch";

export default {
	name: "ProjectSelect",

	components: {
		ProjectEdit,
		ProjectSearch,
	},

	props: {
		defaultProjects: { type: Array, default: () => [] },
		nested: Boolean,
		noActions: Boolean,
		useSessionProjects: Boolean,
		omitCreation: { type: Boolean, default: false },
		// DCTL-2035/DCTL-2495: when set, the paper is added/edited from this
		// project's context - the project is pinned (pre-selected, not removable)
		// but other projects can still be added or removed. Creation stays hidden.
		lockedProject: { type: Object, default: null },
	},
	emits: ["update:dirty", "update:invalid"],

	data() {
		return {
			attachAction: "EXIST",
			attachActions: [
				{
					title: "Create a new project",
					value: "NEW",
				},
				{
					title: "Add to an existing project",
					value: "EXIST",
				},
				{
					title: "Do not associate with project",
					value: "",
				},
			],
			invalid: true,
			projects: [],
			projectSelects: [], // project UUIDs
			subDirty: false,
			subInvalid: true,
			submitting: false,
		};
	},

	computed: {
		...mapState(useMainStore, ["sessionProjects"]),
	},

	watch: {
		attachAction(val, oldVal) {
			if (val !== oldVal) {
				// clear selected projects if attach action changed
				this.projectSelects = [];
			}

			if (!val) {
				this.invalid = false;
			} else {
				// starting conditions
				if (val === "NEW" && this.subInvalid) {
					this.invalid = true;
				} else if (val === "EXIST" && !this.projectSelects.length) {
					this.invalid = true;
				} else {
					this.invalid = false;
				}
			}
		},
		defaultProjects(val) {
			this.selectProjects(val);
		},
		invalid(val) {
			if (val) {
				this.$emit("update:invalid", true);
			} else {
				this.$emit("update:invalid", false);
			}
		},
		projectSelects(val) {
			if (this.attachAction === "EXIST") {
				if (!val.length) {
					this.invalid = true;
				} else {
					this.invalid = false;

					const projectsChanged = !_.isEqual(val, this.defaultProjects);
					this.$emit("update:dirty", projectsChanged);
				}
			}
		},
		subDirty(val) {
			if (val && this.attachAction) {
				this.$emit("update:dirty", true);
			} else {
				this.$emit("update:dirty", false);
			}
		},
		subInvalid(val) {
			if (this.attachAction === "NEW") {
				if (val) {
					this.invalid = true;
				} else {
					this.invalid = false;
				}
			}
		},
	},

	mounted() {
		this.selectProjects();
	},

	methods: {
		...mapActions(useMainStore, [
			"clearSessionProjects",
			"setSessionProjects",
			"showNotification",
		]),
		clear() {
			this.selectProjects();

			if (this.$refs.projectEdit) {
				this.$refs.projectEdit.clear();
			}
		},
		select() {
			const context = this;

			return new Promise((resolve, reject) => {
				if (context.lockedProject) {
					// Pinned to the originating project: always attach to it, plus
					// whatever else is selected.
					resolve(context.withLockedProject(context.projectSelects));
					return;
				}

				if (context.attachAction === "NEW") {
					// create project
					context.$refs.projectEdit
						.saveRequest("add")
						.then((response) => {
							const projects = [response.data.project];

							// store current session projects
							context.setSessionProjects(projects);

							resolve(projects);
						})
						.catch((error) => reject(error));
				} else if (context.attachAction === "EXIST") {
					// store current session projects
					context.setSessionProjects(context.projectSelects);

					// select a project
					resolve(context.projectSelects);
				} else {
					// clear current session projects
					context.clearSessionProjects();

					// do not select a project
					resolve([]);
				}
			});
		},
		selectProjects() {
			// select "EXIST" option for attach action
			this.attachAction = "EXIST";

			if (this.lockedProject) {
				// Pre-fill with the current projects (edit flow) or just the pinned
				// one (add flow); the pinned project is always included, so the
				// selection can never be empty.
				const prefill = this.withLockedProject(this.defaultProjects);
				this.projects = prefill;
				this.projectSelects = [...prefill];
				this.invalid = false;
				return;
			}

			if (!_.isEmpty(this.defaultProjects)) {
				this.projects = [...this.defaultProjects];
				this.projectSelects = [...this.defaultProjects];
				this.invalid = false;
			} else {
				if (this.useSessionProjects) {
					// adding new paper
					if (!_.isEmpty(this.sessionProjects)) {
						// default to cached session project
						this.projects = [...this.sessionProjects];
						this.projectSelects = [...this.sessionProjects];

						this.invalid = false;
					} else {
						this.projects = [];
						this.projectSelects = [];

						this.invalid = true;
					}
				} else {
					// loading existing paper but no projects selected
					this.attachAction = "";
					this.projects = [];
					this.projectSelects = [];

					this.invalid = true;
				}
			}
		},
		// The given projects with the locked (pinned) project unioned in,
		// deduped by uuid.
		withLockedProject(projects) {
			if (
				!this.lockedProject ||
				projects.some((p) => p.uuid === this.lockedProject.uuid)
			) {
				return [...projects];
			}

			return [this.lockedProject, ...projects];
		},
	},
};
</script>

<style scoped lang="scss"></style>
